/**
 * Locale coercion and conversion — single frontend entry point.
 *
 * - App locale: `en` | `fr` (i18n, cookies, tenant settings, Intl)
 * - Exam language: `EN` | `FR` (exam config, interview API, TTS)
 *
 * Always coerce via `normalizeLocale` / `normalizeExamLanguage` instead of
 * ad-hoc `startsWith('fr')`, `toLowerCase()`, or inline `en-US` / `fr-FR`.
 */
import type { TFunction } from 'i18next';

import i18n from '@/i18n';
import { FALLBACK_LOCALE, normalizeLocale, type Locale } from '@/i18n/config';
import {
  DEFAULT_EXAM_LANGUAGE,
  type ExamLanguage,
  isExamLanguage,
} from '@/types/exam-config.types';
import type { CandidateAssessmentSummary } from '@/types/job.types';

export { FALLBACK_LOCALE, normalizeLocale, type Locale } from '@/i18n/config';

/** BCP-47 tags for Intl / browser APIs only — never store or compare these in business logic. */
export type Bcp47Locale = 'en-US' | 'fr-FR';

const BCP47_BY_APP_LOCALE: Record<Locale, Bcp47Locale> = {
  en: 'en-US',
  fr: 'fr-FR',
};

/** App locale → BCP-47 tag (Intl, date-fns locale, Azure voice region, etc.). */
export function toBcp47Locale(locale: Locale): Bcp47Locale {
  return BCP47_BY_APP_LOCALE[locale];
}

/** BCP 47 locale tag for Intl formatters, derived from the active i18n language. */
export function getIntlLocale(): Bcp47Locale {
  return toBcp47Locale(normalizeLocale(i18n.language));
}

/**
 * App locale (`en` | `fr`) → exam config / interview API language (`EN` | `FR`).
 * Mirror of backend `exam_language_from_locale`.
 */
export function localeToExamLanguage(locale: Locale): ExamLanguage {
  return locale === 'fr' ? 'FR' : 'EN';
}

/**
 * Coerce arbitrary input (`EN`, `en`, `en-US`, `fr_FR`, …) to exam language (`EN` | `FR`).
 * Mirror of backend `normalize_exam_language`.
 */
export function normalizeExamLanguage(value: unknown): ExamLanguage {
  if (isExamLanguage(value)) return value;
  return localeToExamLanguage(normalizeLocale(value));
}

/** Exam config / interview language (`EN` | `FR`) → app locale (`en` | `fr`). */
export function examLanguageToLocale(examLanguage: string | null | undefined): Locale {
  return normalizeLocale(examLanguage);
}

/** CV/document source language for content formatting (not recruiter UI). */
export function resolveContentLocale(contentLocale?: string | null): Locale {
  if (contentLocale != null && String(contentLocale).trim()) {
    return normalizeLocale(contentLocale);
  }
  return normalizeLocale(i18n.language);
}

/** Assessment content locale from persisted metadata, with psychometric fallback. */
export function getAssessmentContentLocale(
  assessment: CandidateAssessmentSummary | null | undefined,
): Locale {
  const raw = (assessment?.skill_scores as Record<string, unknown> | undefined)?.content_locale;
  if (raw != null && String(raw).trim()) {
    return normalizeLocale(raw);
  }

  const psychometricLanguage = assessment?.psychometric_results?.disc?.language;
  if (psychometricLanguage != null && String(psychometricLanguage).trim()) {
    return normalizeLocale(psychometricLanguage);
  }

  return FALLBACK_LOCALE;
}

/** i18n translator fixed to exam language (`job` namespace), independent of UI locale. */
export function getFixedTForExamLanguage(
  examLanguage: string | null | undefined,
): TFunction<'job'> {
  return i18n.getFixedT(
    examLanguageToLocale(normalizeExamLanguage(examLanguage ?? DEFAULT_EXAM_LANGUAGE)),
    'job',
  ) as TFunction<'job'>;
}

/** i18n translator fixed to content locale (`candidates` namespace). */
export function getFixedTForContentLocale(
  contentLocale: string | null | undefined,
): TFunction<'candidates'> {
  return i18n.getFixedT(resolveContentLocale(contentLocale), 'candidates') as TFunction<'candidates'>;
}

/** Intl locale for candidate content (CV source language), else UI locale. */
export function getIntlLocaleForContent(contentLocale?: string | null): Bcp47Locale {
  return toBcp47Locale(resolveContentLocale(contentLocale));
}

/** Translate a key in the CV source language, independent of UI locale. */
export function tForContent(
  key: string,
  contentLocale?: string | null,
  options?: Record<string, unknown>,
): string {
  // `key` is resolved at runtime, so it can't satisfy i18next's literal-key typing.
  return i18n.t(key as never, { ...options, lng: resolveContentLocale(contentLocale) });
}

/** Locale for currency display (MAD uses Moroccan formatting). */
export function getCurrencyIntlLocale(currency: string): string {
  return currency === 'MAD' ? 'fr-MA' : getIntlLocale();
}

/** Locale-aware number formatting (grouping separators, etc.). */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(getIntlLocale(), options).format(value);
}

/** Locale-aware currency formatting. */
export function formatCurrency(
  amount: number,
  currency: string,
  options: Pick<Intl.NumberFormatOptions, 'minimumFractionDigits' | 'maximumFractionDigits'> = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  },
): string {
  return new Intl.NumberFormat(getCurrencyIntlLocale(currency), {
    style: 'currency',
    currency,
    ...options,
  }).format(amount);
}
